import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './context/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { initSentry } from './utils/sentry';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import Contact from './pages/Contact';
import About from './pages/About';
import ContractorDashboard from './pages/ContractorDashboard';
import HomeownerDashboard from './pages/HomeownerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import SystemMonitor from './pages/SystemMonitor';
import ProjectView from './pages/ProjectView';
import EstimateBuilder from './pages/EstimateBuilder';
import EstimateReview from './pages/EstimateReview';
import EstimatePrintView from './pages/EstimatePrintView';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import HomeownerWelcome from './pages/HomeownerWelcome';
import TemplatesPage from './pages/TemplatesPage';

// Initialize Sentry
initSentry();

const theme = createTheme({
  palette: {
    primary: {
      main: '#0d47a1',
      light: '#5472d3',
      dark: '#002171',
    },
    secondary: {
      main: '#42a5f5',
      light: '#80d6ff',
      dark: '#0077c2',
    },
    success: {
      main: '#66bb6a',
      light: '#98ee99',
    },
    error: {
      main: '#ef5350',
    },
    warning: {
      main: '#ffa726',
    },
    background: {
      default: '#f5f7fa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
  },
});

function App() {
  return (
    <ErrorBoundary name="App">
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<About />} />
            <Route path="/welcome" element={<HomeownerWelcome />} />

            <Route
              path="/contractor"
              element={
                <PrivateRoute allowedRoles={['contractor']}>
                  <ContractorDashboard />
                </PrivateRoute>
              }
            />

            <Route
              path="/homeowner"
              element={
                <PrivateRoute allowedRoles={['homeowner']}>
                  <HomeownerDashboard />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <AdminDashboard />
                </PrivateRoute>
              }
            />

            <Route
              path="/system-monitor"
              element={
                <PrivateRoute allowedRoles={['admin']}>
                  <SystemMonitor />
                </PrivateRoute>
              }
            />

            <Route
              path="/templates"
              element={
                <PrivateRoute allowedRoles={['contractor', 'admin']}>
                  <TemplatesPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/project/:id"
              element={
                <PrivateRoute allowedRoles={['contractor', 'homeowner', 'admin']}>
                  <ProjectView />
                </PrivateRoute>
              }
            />

            <Route
              path="/estimate/:id"
              element={
                <PrivateRoute allowedRoles={['contractor', 'admin']}>
                  <EstimateBuilder />
                </PrivateRoute>
              }
            />

            <Route
              path="/estimate-review/:id"
              element={
                <PrivateRoute allowedRoles={['homeowner', 'admin']}>
                  <EstimateReview />
                </PrivateRoute>
              }
            />

            <Route
              path="/estimate-print/:id"
              element={
                <PrivateRoute allowedRoles={['contractor', 'homeowner', 'admin']}>
                  <EstimatePrintView />
                </PrivateRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
